import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { PortalBranding } from '../branding/types';
import {
  applyBrandingCssVars,
  defaultBranding,
  DEFAULT_LOGO_DARK,
  DEFAULT_LOGO_LIGHT,
  resolveMediaUrl,
} from '../branding/applyBrandingCss';
import { useDarkMode } from './DarkModeContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface BrandingContextValue {
  branding: PortalBranding;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logoSrc: string;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export type { PortalBranding } from '../branding/types';

async function fetchBranding(): Promise<PortalBranding> {
  const { data } = await axios.get<PortalBranding>(`${API_URL}/branding`);
  return {
    logo_light_url: data.logo_light_url ?? null,
    logo_dark_url: data.logo_dark_url ?? null,
    color_mode: data.color_mode === 'custom' ? 'custom' : 'brand',
    custom_accent_light: data.custom_accent_light || defaultBranding().custom_accent_light,
    custom_accent_dark: data.custom_accent_dark || defaultBranding().custom_accent_dark,
  };
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { darkMode } = useDarkMode();
  const [branding, setBranding] = useState<PortalBranding>(defaultBranding());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const next = await fetchBranding();
      setBranding(next);
    } catch (e) {
      console.error('Failed to load branding', e);
      setError('Could not load portal branding');
      setBranding(defaultBranding());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    applyBrandingCssVars(branding, darkMode);
  }, [branding, darkMode]);

  const logoSrc = useMemo(
    () =>
      resolveMediaUrl(
        darkMode ? branding.logo_dark_url : branding.logo_light_url,
        darkMode ? DEFAULT_LOGO_DARK : DEFAULT_LOGO_LIGHT
      ),
    [branding.logo_dark_url, branding.logo_light_url, darkMode]
  );

  const value = useMemo(
    () => ({
      branding,
      loading,
      error,
      refresh,
      logoSrc,
    }),
    [branding, loading, error, refresh, logoSrc]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return ctx;
}
